import { CrudFilters, CrudOperators, DataProvider } from "@pankod/refine-core";
import restDataProvider from "@pankod/refine-simple-rest";
import { AxiosInstance } from "axios";

import { ADMIN_BFF_URL } from "./environment";

const mapOperator = (operator: CrudOperators): string => {
  switch (operator) {
    case "eq":
      return "";
    case "contains":
      return "";
    case "in":
      return "";
    case "ne":
      return "";
    default:
      throw new Error(`Operator ${operator} is not supported`);
  }
};

const generateFilter = (filters?: CrudFilters) => {
  const queryFilters: { [key: string]: string } = {};
  if (filters) {
    filters.forEach((filter: any) => {
      if (filter.operator !== "or") {
        const { field, operator, value } = filter;

        const mappedOperator = mapOperator(operator);
        queryFilters[`${field}${mappedOperator}`] = value;
      }
    });
  }

  return queryFilters;
};

export const dataProvider = (axios: AxiosInstance): DataProvider => {
  return {
    ...restDataProvider(ADMIN_BFF_URL, axios),
    getList: async ({
      resource,
      pagination,
      filters,
      hasPagination,
      metaData,
      sort,
    }) => {
      const baseUrl = `${ADMIN_BFF_URL}/${resource}`;
      const urlResource = metaData?.URLSuffix
        ? `${baseUrl}/${metaData.URLSuffix}`
        : `${baseUrl}/`;

      const current = pagination?.current || 1;
      const pageSize = pagination?.pageSize || 100;

      const querySorters: { [key: string]: string } = {};

      if (sort && sort.length > 0) {
        const { field, order } = sort[0];
        querySorters["sort"] = field;
        querySorters["order"] = order;
      }

      const queryFilters = generateFilter(filters);

      const query: Record<string, string> = {};

      if (hasPagination) {
        query["page"] = String(current);
        query["size"] = String(pageSize);
      }

      let url = new URL(urlResource);

      const queryParams = [
        ...Object.entries(metaData?.query ?? {}),
        ...Object.entries(query),
        ...Object.entries(queryFilters),
        ...Object.entries(querySorters),
      ];

      for (const [key, value] of queryParams) {
        url.searchParams.set(key, String(value));
      }

      let data: any;

      let response: any;
      if (metaData?.usePost) {
        const updatedRequestDate = {
          ...queryFilters,
          ...querySorters,
          page: current,
          size: pageSize,
        };

        response = await axios.post(urlResource, updatedRequestDate);
        data = response.data;
      } else {
        response = await axios.get(url.toString());
        data = response.data;
      }

      if (data.items === undefined && !hasPagination) {
        const items = Array.isArray(data) ? data : [];
        return {
          data: items,
          total: items.length,
        };
      }

      if (data.items === undefined && Array.isArray(data)) {
        return {
          data,
          total: data.length,
        };
      }

      const items = Array.isArray(data.items) ? data.items : [];
      return {
        data: items,
        total: data.total,
      };
    },
    getOne: async ({ resource, id, metaData }) => {
      let finalResource = resource;
      if (resource === "v1/branch-office/linked-places") {
        finalResource = "v1/branch-office";
      }

      let url = metaData?.getComments
        ? `${ADMIN_BFF_URL}/${finalResource}/${id}/comments`
        : `${ADMIN_BFF_URL}/${finalResource}/${id}`;

      if (metaData?.query) {
        const newUrl = new URL(url);
        for (const [key, value] of Object.entries(metaData?.query)) {
          newUrl.searchParams.set(key, value as string);
        }
        url = newUrl.toString();
      }

      const { data } = await axios.get(url);

      return {
        data,
      };
    },
    getMany: async ({ resource, ids }) => {
      let url = `${ADMIN_BFF_URL}/${resource}`;
      if (ids.length === 1) {
        url = `${ADMIN_BFF_URL}/${resource}/${ids[0]}`;
      }
      const { data } = await axios.get(url);
      return {
        data,
      };
    },
    create: async ({ resource, variables, metaData }) => {
      const baseUrl = `${ADMIN_BFF_URL}/${resource}`;
      const { URLSuffix, skipTrailingSlash } = metaData ?? {};

      const trailingSlash = skipTrailingSlash ? "" : "/";

      const url = URLSuffix
        ? `${baseUrl}/${URLSuffix}`
        : `${baseUrl}${trailingSlash}`;

      const { data } = await axios.post(url, variables);

      return {
        data,
      };
    },
    update: async ({ resource, id, variables, metaData }) => {
      const url = metaData?.URLSuffix
        ? `${ADMIN_BFF_URL}/${resource}/${id}/${metaData.URLSuffix}`
        : `${ADMIN_BFF_URL}/${resource}/${id}`;

      if (resource.endsWith("users") || resource === "v1/repique-campaign") {
        const { data } = await axios.patch(url, variables);
        return { data };
      }

      if (metaData?.URLSuffix && metaData?.method) {
        const { data } = await axios({
          method: metaData.method,
          url,
          data: variables,
        });
        return { data };
      }

      if (metaData?.URLSuffix) {
        const { data } = await axios.post(url);
        return { data };
      }

      const method = metaData?.method || "PUT";

      const { data } = await axios({
        method,
        url,
        data: variables,
      });

      return { data };
    },

    deleteOne: async ({ resource, id, metaData }) => {
      let url = metaData?.URLSuffix
        ? `${ADMIN_BFF_URL}/${resource}/${id}/${metaData.URLSuffix}`
        : `${ADMIN_BFF_URL}/${resource}/${id}`;

      if (metaData?.query) {
        const newUrl = new URL(url);
        for (const [key, value] of Object.entries(metaData?.query)) {
          newUrl.searchParams.set(key, value as string);
        }
        url = newUrl.toString();
      }

      const { data } = await axios.delete(url);

      return {
        data,
      };
    },
  };
};
